import { useEffect, useState } from "react";
import { flowRun, type QpAdvice, type QpComposition, type QpPdf } from "../../api";
import { Cta } from "./PhoneFrame";

// The retail payoff: the matched model portfolio rendered as an allocation
// donut + holdings, then Confirm → submit the flow → generate the advice PDF.

const PALETTE = [
  "#1F3447", "#5C7A66", "#E8B65A", "#3E5266", "#C8923B",
  "#6E7C8C", "#9BB0A2", "#B5453B", "#8CA0B4", "#D8C08A",
];

function byAssetClass(positions: QpComposition["positions"]) {
  const map = new Map<string, number>();
  for (const p of positions) {
    map.set(p.assetClassName, (map.get(p.assetClassName) ?? 0) + (p.weight ?? 0));
  }
  return [...map.entries()]
    .map(([name, weight]) => ({ name, weight }))
    .sort((a, b) => b.weight - a.weight);
}

function Donut({ slices }: { slices: { name: string; weight: number }[] }) {
  const total = slices.reduce((s, x) => s + x.weight, 0) || 1;
  const R = 52;
  const C = 2 * Math.PI * R;
  let offset = 0;
  return (
    <svg width="150" height="150" viewBox="0 0 150 150" role="img" aria-label="Allocation">
      <g transform="rotate(-90 75 75)">
        {slices.map((s, i) => {
          const frac = s.weight / total;
          const len = frac * C;
          const el = (
            <circle
              key={s.name}
              cx="75"
              cy="75"
              r={R}
              fill="none"
              stroke={PALETTE[i % PALETTE.length]}
              strokeWidth="20"
              strokeDasharray={`${len} ${C - len}`}
              strokeDashoffset={-offset}
            />
          );
          offset += len;
          return el;
        })}
      </g>
      <text x="75" y="71" textAnchor="middle" fontSize="13" fill="#6E7C8C">Assets</text>
      <text x="75" y="90" textAnchor="middle" fontSize="20" fontWeight="700" fill="#13202E">
        {slices.length}
      </text>
    </svg>
  );
}

export function AdviceScreen({ runId, onExit }: { runId: number; onExit: () => void }) {
  const [advice, setAdvice] = useState<QpAdvice | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [phase, setPhase] = useState<"advice" | "confirming" | "done">("advice");
  const [pdf, setPdf] = useState<QpPdf | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [blocked, setBlocked] = useState(false); // flow not submittable yet

  useEffect(() => {
    flowRun
      .advice(runId)
      .then((d) => setAdvice(d.advice))
      .catch((e) => setErr(e.message));
  }, [runId]);

  if (err) return <div className="iapp-view"><div className="iapp-error">{err}</div></div>;
  if (!advice) {
    return (
      <div className="iapp-center">
        <div className="iapp-spinner" />
        <span>Building your advice…</span>
      </div>
    );
  }

  const goal = advice.goals[0];
  const comp = goal?.composition;
  const positions = comp?.positions ?? [];
  const slices = byAssetClass(positions);

  if (!comp || positions.length === 0) {
    return (
      <div className="iapp-view">
        <h2>Almost there</h2>
        <div className="iapp-note">
          {comp?.modelResolutionFailure === "no_risk_score"
            ? "Finish the risk questions and we'll match your portfolio."
            : comp?.modelResolutionFailure
              ? `No model portfolio matched (${comp.modelResolutionFailure}). Your advisor can help.`
              : "Your portfolio isn't ready yet."}
        </div>
      </div>
    );
  }

  async function confirm() {
    setPhase("confirming");
    setNotice(null);
    // Submit must succeed before a PDF can render — QP refuses /pdfs on a draft
    // instance. If the flow isn't fully complete (e.g. the Purpose/goal_input
    // step QP can't currently complete over the API), we stop cleanly and keep
    // the recommendation on screen rather than throwing the raw draft error.
    try {
      await flowRun.submit(runId);
    } catch {
      setNotice(
        "Your recommendation is ready. Finalising the signed advice document needs a last step that's completed in Quantfolio — it'll appear here once that's in place.",
      );
      setBlocked(true);
      setPhase("advice");
      return;
    }
    try {
      const gen = await flowRun.pdf(runId);
      const doc = gen.data.find((p) => p.status === "completed" || p.url) ?? gen.data[0];
      if (doc) {
        setPdf(doc);
        try {
          const link = await flowRun.pdfUrl(runId, doc.id);
          setPdfUrl(link.url);
        } catch {
          setPdfUrl(doc.url);
        }
      }
      setPhase("done");
    } catch (e) {
      setNotice((e as Error).message);
      setPhase("advice");
    }
  }

  async function sign() {
    // Hand the completed session to QP for signing: open the SSO deeplink in a
    // new window, then return to the investor selector.
    try {
      const { jump } = await flowRun.sign(runId);
      if (jump.jump_url) window.open(jump.jump_url, "_blank", "noopener,noreferrer");
    } catch {
      /* even if logging the jump fails, don't trap the user here */
    }
    onExit();
  }

  // Final screen: advice ready → review the document → sign.
  if (phase === "done") {
    return (
      <>
        <div className="iapp-view">
          <div className="iapp-advice-hero">
            <span className="kicker">All done</span>
            <h2>Here's your investment advice</h2>
          </div>
          <p className="lede">
            We've prepared your personal suitability report from your answers.
            Review it below, then sign to finalise your advice.
          </p>
          {pdf ? (
            <a className="iapp-pdf" href={pdfUrl ?? pdf.url} target="_blank" rel="noreferrer">
              <span className="ic">PDF</span>
              <span className="nm">
                {pdf.templateName}
                <small>Tap to open · generated just now</small>
              </span>
            </a>
          ) : (
            <div className="iapp-note">No document was produced.</div>
          )}
        </div>
        <Cta label="Sign" onClick={sign} />
      </>
    );
  }

  return (
    <>
      <div className="iapp-view">
        <div className="iapp-advice-hero">
          <span className="kicker">Your recommendation</span>
          <h2>{comp.modelPortfolioName ?? goal.goalName}</h2>
        </div>

        <div className="iapp-donut">
          <Donut slices={slices} />
        </div>

        <div className="iapp-section-label">Fund mix</div>
        <div className="iapp-group">
          {positions.map((p) => (
            <div className="iapp-holding" key={p.productId}>
              <span className="nm">
                {p.name}
                <small>{p.isin} · {p.assetClassName}</small>
              </span>
              <span className="wt">{p.weight.toFixed(0)}%</span>
            </div>
          ))}
        </div>

        <div className="iapp-section-label">Asset mix</div>
        <div className="iapp-group">
          {slices.map((s, i) => (
            <div className="iapp-holding" key={s.name}>
              <span className="sw" style={{ background: PALETTE[i % PALETTE.length] }} />
              <span className="nm">{s.name}</span>
              <span className="wt">{s.weight.toFixed(0)}%</span>
            </div>
          ))}
        </div>

        {notice && <div className="iapp-note" style={{ marginTop: 16 }}>{notice}</div>}
      </div>
      {!blocked && (
        <Cta
          label={phase === "confirming" ? "Preparing your advice…" : "Confirm & get my advice"}
          onClick={confirm}
          disabled={phase === "confirming"}
        />
      )}
    </>
  );
}
